/**
 * The installed app's launch scene (components/launch/LaunchScene.jsx), as
 * plain functions of time: which part of the scene is on (`frameAt`) and
 * where everything is drawn (`poseAt`). No DOM here, so every moment of it
 * is checked in launchScene.test.js.
 *
 * The parrot flies in from off-screen, wings beating, and lands on a loading
 * bar. It sits there for exactly as long as the app takes to get ready — the
 * session check, `authStore.bootstrap` — and the bar meanwhile shows only
 * that something is happening, never a fake percentage. Then the bar fills,
 * the parrot crouches and flaps off the other side while the navy fades into
 * the first screen. With reduced motion it is just the still parrot, then a
 * fade.
 *
 * The same script as the Android app's LaunchChoreography.kt (tooti#241),
 * number for number, so the two launches look alike; change both together.
 */

/**
 * The class index.html's inline script puts on <html> when this page load is
 * a launch of the installed app that has not played the scene yet; the
 * scene takes it off when it is over.
 */
export const LAUNCHING_CLASS = 'launching'

/** Whether this page load plays the scene (decided before the first paint, see index.html). */
export function launchScenePending() {
  return typeof document !== 'undefined' && document.documentElement.classList.contains(LAUNCHING_CLASS)
}

/** Fly-in, landing and settling: the parrot is perched and still from here. */
export const FLY_IN_MS = 820
/** The bar fills once the app is ready; the parrot crouches meanwhile. */
export const FILL_MS = 260
/** Take-off, and the navy fading into the first screen underneath. */
export const FLY_OFF_MS = 640
/** Reduced motion: the still parrot and the navy fade out together. */
export const FADE_MS = 280
/** The parrot takes off by now even if the app never reports ready. */
export const CEILING_MS = 4000

/** Touchdown on the bar, inside FLY_IN_MS. */
const LAND_MS = 680
/** One wing beat in flight. */
const FLAP_MS = 170
/** One pass of the waiting bar's segment. */
const SWEEP_MS = 1100
/** The waiting bar's segment, as a fraction of the bar. */
const SEGMENT = 0.32

/**
 * The moment `scene` ms after the scene's first frame, given the scene time
 * at which the app reported ready (`readyAt`, null while it has not):
 * `{ phase, t, scene }`, `t` being the ms into `phase`. Phases: FlyIn, Perch,
 * Fill, FlyOff, then Done; with reduced motion Still, Fade, Done.
 */
export function frameAt(scene, readyAt, reducedMotion) {
  const go = Math.min(readyAt ?? Infinity, CEILING_MS)
  if (reducedMotion) {
    if (scene < go) return { phase: 'Still', t: scene, scene }
    if (scene < go + FADE_MS) return { phase: 'Fade', t: scene - go, scene }
    return { phase: 'Done', t: 0, scene }
  }
  if (scene < FLY_IN_MS) return { phase: 'FlyIn', t: scene, scene }
  // Ready during the fly-in: land first, then go straight on — no wait.
  const fillAt = Math.max(FLY_IN_MS, go)
  if (scene < fillAt) return { phase: 'Perch', t: scene - FLY_IN_MS, scene }
  if (scene < fillAt + FILL_MS) return { phase: 'Fill', t: scene - fillAt, scene }
  if (scene < fillAt + FILL_MS + FLY_OFF_MS) return { phase: 'FlyOff', t: scene - fillAt - FILL_MS, scene }
  return { phase: 'Done', t: 0, scene }
}

/** At rest on the bar: the still parrot, no bar (reduced motion's whole scene). */
const STILL = Object.freeze({
  x: 0, y: 0, tilt: 0, squash: 1, wing: 0, wingFar: 0, eye: 1, jaw: 0,
  birdAlpha: 1, navyAlpha: 1, barAlpha: 0, barFrom: 0, barTo: 0,
})

/**
 * Everything drawn at `frame`. `x`, `y`: where the parrot's toes are, in px
 * from the top centre of the bar (y down); `tilt` turns the whole bird about
 * its middle (degrees). The joints: `squash` the body's height on its feet,
 * `wing`/`wingFar` the angles at the shoulder, `eye` its height (1 open),
 * `jaw` the lower beak. Then each layer's opacity, and the bar's filled part
 * from `barFrom` to `barTo` as fractions of its width. `offstage` is how far
 * from the perch, in px, the parrot is out of sight sideways.
 */
export function poseAt(frame, offstage) {
  const { phase, t, scene } = frame
  // Time on the bar so far, for the idle breathing and blinking.
  const sat = scene - FLY_IN_MS
  switch (phase) {
    case 'Still':
      return { ...STILL }
    case 'Fade': {
      const left = 1 - smooth(t / FADE_MS)
      return { ...STILL, birdAlpha: left, navyAlpha: left }
    }
    case 'Done':
      return { ...STILL, birdAlpha: 0, navyAlpha: 0 }
    case 'FlyIn': {
      // Eases into the perch, but slowly enough that the flight is seen.
      const s = 1 - square(1 - Math.min(t / LAND_MS, 1))
      const fold = 1 - smooth((t - 600) / 180)
      const settle = smooth((t - LAND_MS) / (FLY_IN_MS - LAND_MS))
      const flare = smooth((t / LAND_MS - 0.65) / 0.35)
      const [from, to] = waiting(scene)
      return {
        x: bezier(-offstage, -130, 0, s),
        y: bezier(-300, -10, 0, s),
        // Leaning into the flight, pitching back to brake, upright on the bar.
        tilt: lerp(lerp(14, -12, flare), 0, settle),
        squash: touchdown(t),
        wing: flap(t, 25, 50, 0) * fold,
        wingFar: flap(t, 30, 60, -0.4) * fold,
        eye: 1,
        jaw: chirp(scene),
        birdAlpha: 1,
        navyAlpha: 1,
        barAlpha: smooth(t / 250),
        barFrom: from,
        barTo: to,
      }
    }
    case 'Perch': {
      const [from, to] = waiting(scene)
      return { ...STILL, squash: breath(sat), eye: blink(sat), jaw: chirp(scene), barAlpha: 1, barFrom: from, barTo: to }
    }
    case 'Fill': {
      const [from, to] = waiting(scene - t)
      const fill = easeOut(t / FILL_MS)
      return {
        ...STILL,
        squash: lerp(breath(sat), 0.9, smooth(t / FILL_MS)),
        eye: blink(sat),
        jaw: chirp(scene),
        barAlpha: 1,
        barFrom: lerp(from, 0, fill),
        barTo: lerp(to, 1, fill),
      }
    }
    case 'FlyOff': {
      const s = square(t / FLY_OFF_MS)
      const beat = smooth(t / 100)
      return {
        x: bezier(0, 70, offstage, s),
        y: bezier(0, -30, -360, s),
        tilt: 16 * smooth(t / 250),
        // The push off the bar, then the stretch settles.
        squash: t < 90 ? lerp(0.9, 1.06, smooth(t / 90)) : lerp(1.06, 1, smooth((t - 90) / 130)),
        wing: flap(t, 25, 50, 0, 150) * beat,
        wingFar: flap(t, 30, 60, -0.4, 150) * beat,
        eye: blink(sat),
        jaw: chirp(scene),
        birdAlpha: 1,
        navyAlpha: 1 - smooth((t - 140) / 320),
        barAlpha: 1 - smooth((t - 40) / 200),
        barFrom: 0,
        barTo: 1,
      }
    }
    default:
      throw new Error(`unknown launch phase ${phase}`)
  }
}

/** A wing's angle in flight: `mid` ± `swing` degrees, the tip rising first. */
function flap(t, mid, swing, lag, period = FLAP_MS) {
  return mid + swing * Math.sin((2 * Math.PI * t) / period + lag)
}

/** The body on landing: squashes onto the bar and springs back. */
function touchdown(t) {
  if (t < LAND_MS) return 1
  if (t < LAND_MS + 80) return lerp(1, 0.9, smooth((t - LAND_MS) / 80))
  return lerp(0.9, 1, smooth((t - LAND_MS - 80) / (FLY_IN_MS - LAND_MS - 80)))
}

/** Sitting still, breathing. */
function breath(sat) {
  return 1 + 0.015 * Math.sin((2 * Math.PI * sat) / 1500)
}

/** A blink every 1.7 s on the bar, the first one soon after landing. */
function blink(sat) {
  if (sat < 450) return 1
  const k = (sat - 450) % 1700
  if (k < 60) return lerp(1, 0.1, k / 60)
  if (k < 150) return lerp(0.1, 1, (k - 60) / 90)
  return 1
}

/** The beak opens once, just after touchdown. */
function chirp(scene) {
  const a = scene - LAND_MS - 20
  if (a < 0) return 0
  if (a < 70) return 14 * smooth(a / 70)
  if (a < 160) return 14 * (1 - smooth((a - 70) / 90))
  return 0
}

/** The waiting bar: a segment sweeping left to right, clipped to the bar. */
function waiting(scene) {
  const head = lerp(-SEGMENT, 1, (scene % SWEEP_MS) / SWEEP_MS)
  return [clamp(head), clamp(head + SEGMENT)]
}

const bezier = (a, control, b, s) => (1 - s) * (1 - s) * a + 2 * (1 - s) * s * control + s * s * b
const clamp = (x) => Math.min(Math.max(x, 0), 1)
const smooth = (x) => {
  const c = clamp(x)
  return c * c * (3 - 2 * c)
}
const easeOut = (x) => 1 - (1 - clamp(x)) ** 3
const lerp = (a, b, k) => a + (b - a) * k
const square = (x) => x * x
